import { TextInput } from "./TextInput";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ButtonGroup } from "./ButtonGroup";
import inheritMarginOrTargetPrice, { marginOrTargetPrice_getValueText, marginOrTargetPrice_getValueText_margin, marginOrTargetPrice_getValueText_target } from "../../../common/inheritMarginOrTargetPrice";
import ClickPopover from "./ClickPopover";
import { formatCurrency } from "../../utils/currency";
import { MdSettings } from "react-icons/md";
import { Modal } from "./Modal";
import cx from "classnames";
import Button from "./Button";
import { FaUndo } from "react-icons/fa";
import { PriceRoundingMethod } from "../../../codegen/graphql";

export default function SetMarginOrTargetPrice({
  data,

  setForKey,
  onUpdate,
}: {
  data: Parameters<typeof inheritMarginOrTargetPrice>[0];

  setForKey: string;
  onUpdate: (data: {
    marginPercentOnBaseCost?: number | null;
    targetListingPriceIncludingVat?: number | null;
    listingPriceRoundingMethod?: PriceRoundingMethod | null;
  }) => void;
}) {
  // Margin or target prices
  const marginOrTargetPrices = useMemo(() => inheritMarginOrTargetPrice(data), [data]);
  const currentMarginOrTargetPrice = useMemo(() => {
    return marginOrTargetPrices.possibles.find((possible) => possible.entityKey === setForKey)!;
  }, [marginOrTargetPrices]);

  const usedIsCurrent = useMemo(() => {
    return currentMarginOrTargetPrice.entityKey === marginOrTargetPrices.selected.entityKey;
  }, [currentMarginOrTargetPrice.entityKey, marginOrTargetPrices.selected.entityKey]);


  const [type, setType] = useState<'MARGIN' | 'TARGET_PRICE'>(
    currentMarginOrTargetPrice.targetListingPriceIncludingVat ? 'TARGET_PRICE' : 'MARGIN',
  );
  const [showInheritedValuesModal, setShowInheritedValuesModal] = useState(false);
  const marginPercentOnBaseCostInputRef = useRef<HTMLInputElement>(null);
  const targetListingPriceIncludingVatInputRef = useRef<HTMLInputElement>(null);
  const listingPriceRoundingMethodInputRef = useRef<HTMLSelectElement>(null);

  useLayoutEffect(() => {
    if (marginPercentOnBaseCostInputRef.current) {
      marginPercentOnBaseCostInputRef.current.value = currentMarginOrTargetPrice.marginPercentOnBaseCost?.toString() || '';
    }
    if (targetListingPriceIncludingVatInputRef.current) {
      targetListingPriceIncludingVatInputRef.current.value = currentMarginOrTargetPrice.targetListingPriceIncludingVat?.toString() || '';
    }

    if (listingPriceRoundingMethodInputRef.current) {
      listingPriceRoundingMethodInputRef.current.value = currentMarginOrTargetPrice.listingPriceRoundingMethod?.toString() || '';
    }
  }, [currentMarginOrTargetPrice]);

  // Event handlers
  const handleChangeType = useCallback((value: 'MARGIN' | 'TARGET_PRICE') => {
    setType(value);
  }, [onUpdate]);

  const handleChangeMarginPercentOnBaseCost = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    onUpdate({
      marginPercentOnBaseCost: e.target.value ? parseFloat(e.target.value) : null,
      targetListingPriceIncludingVat: null,
    });
  }, [onUpdate]);

  const handleChangeRetailPriceRoundingMethod = useCallback((value: PriceRoundingMethod | null) => {
    onUpdate({
      listingPriceRoundingMethod: value,
    });
  }, [onUpdate]);

  const handleChangeTargetRetailPriceIncludingVAT = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    onUpdate({
      marginPercentOnBaseCost: null,
      listingPriceRoundingMethod: null,
      targetListingPriceIncludingVat: e.target.value ? parseFloat(e.target.value) : null,
    });
  }, [onUpdate]);

  const handleReset = useCallback(() => {
    onUpdate({
      marginPercentOnBaseCost: null,
      targetListingPriceIncludingVat: null,
      listingPriceRoundingMethod: null,
    });
  }, [onUpdate]);


  return (<>
    <ClickPopover
      content={<>
        <span className="text-blue-500 text-sm underline" onClick={() => setShowInheritedValuesModal(true)}>
          See all inherited values
        </span>
        <div className="flex flex-row gap-2 items-end">
          <ButtonGroup
            label="Type"
            options={[
              { value: 'MARGIN', label: 'Margin' },
              { value: 'TARGET_PRICE', label: 'Target price' },
            ]}
            value={type}
            onChange={handleChangeType}
          />
          <Button
            preset="danger-light"
            size="sm"
            onClick={handleReset}
          >
            <FaUndo
              size={16}
            />
          </Button>
        </div>
        {type === 'MARGIN' && (<>
          <TextInput
            ref={marginPercentOnBaseCostInputRef}
            label="Margin percent on base cost"
            type="number"
            fullWidth={false}
            placeholder={marginOrTargetPrice_getValueText_margin(marginOrTargetPrices.selected.marginPercentOnBaseCost)}
            defaultValue={currentMarginOrTargetPrice.marginPercentOnBaseCost || ''}
            onBlur={handleChangeMarginPercentOnBaseCost}
          />
          <select
            ref={listingPriceRoundingMethodInputRef}
            name="listingPriceRoundingMethod"
            value={currentMarginOrTargetPrice.listingPriceRoundingMethod || ''}
            onChange={(e) => {
              handleChangeRetailPriceRoundingMethod(
                e.target.value
                  ? (e.target.value as PriceRoundingMethod)
                  : null
              );
            }}
          >
            <option value="">None</option>
            <option value={PriceRoundingMethod.NearestFiftyCents}>Nearest 50 cts</option>
            <option value={PriceRoundingMethod.UpperFiftyCents}>Upper 50 cts</option>
            <option value={PriceRoundingMethod.LowerFiftyCents}>Lower 50 cts</option>
          </select>
        </>)}

        {type === 'TARGET_PRICE' && (
          <TextInput
            ref={targetListingPriceIncludingVatInputRef}
            label="Target retail price including VAT"
            type="number"
            fullWidth={false}
            placeholder={marginOrTargetPrice_getValueText_target(marginOrTargetPrices.selected.targetListingPriceIncludingVat)}
            defaultValue={marginOrTargetPrices.selected.targetListingPriceIncludingVat || ''}
            onBlur={handleChangeTargetRetailPriceIncludingVAT}
          />
        )}
      </>}
    >
      <div
        className={cx(
          "flex flex-col",
          usedIsCurrent ? '' : 'opacity-50',
        )}
      >
        <span className="text-gray-700 text-xs font-bold flex flex-row gap-1">
          <MdSettings size={16} /> MARGIN/TARGET PRICE
        </span>
        <span className="text-gray-700 text-sm flex flex-row gap-1">
          Coming from: {marginOrTargetPrices.selected.entityLabel}
        </span>
        <span className="text-xl text-gray-900">
          {marginOrTargetPrice_getValueText(marginOrTargetPrices.selected)}
        </span>
      </div>
    </ClickPopover>

    {showInheritedValuesModal && (
      <Modal
        title="Inherited values"
        onClose={() => setShowInheritedValuesModal(false)}
      >
        <div className="flex flex-col text-xl">
          {marginOrTargetPrices.possibles.map((possible) => (
            <div
              key={possible.entityKey}
              className={cx(
                'mb-2 border-b border-gray-200',
                possible.entityKey === marginOrTargetPrices.selected.entityKey ? 'text-blue-500 font-bold' : 'text-gray-700',
              )}
            >
              {possible.entityLabel}: {marginOrTargetPrice_getValueText(possible)}
            </div>
          ))}
        </div>
      </Modal>
    )}
  </>);
}